import { useMemo, useState } from "react";
import { FiBook, FiCheck, FiClock, FiEdit3, FiLayers, FiPercent, FiSearch, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import styles from "./ExamBaseFormModal.module.css";
import { formatMinutesAsHours, MAX_EXAM_DURATION_MINUTES } from "../../../utils/duration";

const ExamBaseFormModal = ({
                               isOpen,
                               mode,
                               values,
                               onChange,
                               onClose,
                               onSubmit,
                               saving,
                               error,
                               topicOptions = [],
                               topicsLoading = false,
                               selectedTopicIds = [],
                               onToggleTopic,
                               onToggleGroup,
                           }) => {
    const { t } = useTranslation();
    const [topicSearch, setTopicSearch] = useState("");

    const selectedSet = useMemo(
        () => new Set((selectedTopicIds || []).map(Number)),
        [selectedTopicIds]
    );

    const selectedTopics = useMemo(
        () => topicOptions.filter((tp) => selectedSet.has(Number(tp.id))),
        [topicOptions, selectedSet]
    );

    // Group the (search-filtered) topics by their owning subject so the user can tell which subject
    // each topic belongs to while still selecting freely across subjects.
    const groups = useMemo(() => {
        const norm = topicSearch.trim().toLowerCase();
        const filtered = topicOptions.filter((tp) => {
            if (!norm) return true;
            return (
                (tp.title || "").toLowerCase().includes(norm) ||
                (tp.parentName || "").toLowerCase().includes(norm)
            );
        });
        const byParent = filtered.reduce((acc, tp) => {
            const key = tp.parentName || t("pages.examBaseForm.ungrouped");
            (acc[key] = acc[key] || []).push(tp);
            return acc;
        }, {});
        return Object.keys(byParent)
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ name, topics: byParent[name] }));
    }, [topicOptions, topicSearch, t]);

    const distinctParents = useMemo(
        () => new Set(selectedTopics.map((tp) => tp.parentName || `id:${tp.id}`)),
        [selectedTopics]
    );
    const spansMultipleSubjects = distinctParents.size > 1;
    const availableQuestions = useMemo(
        () => selectedTopics.reduce((sum, tp) => sum + (Number(tp.questionCount) || 0), 0),
        [selectedTopics]
    );

    if (!isOpen) return null;

    const isCreateMode = mode === "create";
    const title = isCreateMode ? t("pages.examBaseForm.createTitle") : t("pages.examBaseForm.editTitle");

    const hasTitle = values.title && values.title !== "";
    const hasDuration = values.durationMinutes && Number(values.durationMinutes) > 0;
    const durationExceedsMax = Number(values.durationMinutes) > MAX_EXAM_DURATION_MINUTES;
    const hasTopics = selectedTopicIds && selectedTopicIds.length > 0;
    const submitDisabled = saving || !hasTitle || !hasDuration || durationExceedsMax || !hasTopics;

    return (
        <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true"
             aria-labelledby="exam-base-modal-title">
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <div className={styles.titleWrap}>
                        <span className={styles.headerIcon}>{isCreateMode ? <FiBook size={22}/> :
                            <FiEdit3 size={22}/>}</span>
                        <h2 id="exam-base-modal-title" className={styles.title}>{title}</h2>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t("pages.examBaseForm.closeAria")}>
                        <FiX size={20}/>
                    </button>
                </div>

                <form onSubmit={onSubmit} className={styles.form}>
                    <div className={styles.formBody}>

                        <div className={styles.formGroup}>
                            <div className={styles.labelRow}>
                                <label className={styles.label}>
                                    <FiLayers size={14} className={styles.labelIcon}/>
                                    {t("pages.examBaseForm.topics")}
                                </label>
                                {selectedTopics.length > 0 && (
                                    <span className={styles.countBadge}>{selectedTopics.length}</span>
                                )}
                            </div>

                            {selectedTopics.length > 0 && (
                                <div className={styles.selectedChips}>
                                    {selectedTopics.map((tp) => (
                                        <span key={tp.id} className={styles.chip}>
                                            <FiCheck size={13} className={styles.chipIcon}/>
                                            {tp.parentName ? `${tp.parentName} › ${tp.title}` : tp.title}
                                            <button
                                                type="button"
                                                className={styles.chipRemove}
                                                onClick={() => onToggleTopic && onToggleTopic(tp.id)}
                                                aria-label={t("pages.examBaseForm.removeTopic")}
                                            >
                                                <FiX size={12}/>
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className={styles.topicSearchWrap}>
                                <FiSearch size={15} className={styles.topicSearchIcon}/>
                                <input
                                    type="text"
                                    className={styles.topicSearch}
                                    placeholder={t("pages.examBaseForm.searchTopics")}
                                    value={topicSearch}
                                    onChange={(e) => setTopicSearch(e.target.value)}
                                    disabled={topicsLoading}
                                />
                            </div>

                            <div className={styles.topicGroupList} role="group" aria-label={t("pages.examBaseForm.topics")}>
                                {topicsLoading ? (
                                    <div className={styles.pickerEmpty}>{t("pages.examBaseForm.subjectsLoading")}</div>
                                ) : groups.length === 0 ? (
                                    <div className={styles.pickerEmpty}>{t("pages.examBaseForm.noTopics")}</div>
                                ) : (
                                    groups.map((group) => {
                                        const groupIds = group.topics.map((tp) => Number(tp.id));
                                        const allInGroupSelected = groupIds.every((id) => selectedSet.has(id));
                                        return (
                                        <div key={group.name} className={styles.topicGroup}>
                                            <div className={styles.topicGroupHeader}>
                                                <span className={styles.topicGroupName}>{group.name}</span>
                                                {group.topics.length > 1 && onToggleGroup && (
                                                    <button
                                                        type="button"
                                                        className={styles.groupSelectAll}
                                                        onClick={() => onToggleGroup(groupIds, !allInGroupSelected)}
                                                    >
                                                        {allInGroupSelected
                                                            ? t("pages.examBaseForm.clearAllInGroup")
                                                            : t("pages.examBaseForm.selectAllInGroup")}
                                                    </button>
                                                )}
                                            </div>
                                            {group.topics.map((tp) => {
                                                const isSelected = selectedSet.has(Number(tp.id));
                                                return (
                                                    <label
                                                        key={tp.id}
                                                        className={`${styles.topicCheckRow} ${isSelected ? styles.topicCheckRowSelected : ""}`}
                                                    >
                                                        <span className={styles.topicCheckboxWrap}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => onToggleTopic && onToggleTopic(tp.id)}
                                                                className={styles.topicCheckbox}
                                                            />
                                                            {isSelected && <FiCheck size={13} className={styles.topicCheckmark}/>}
                                                        </span>
                                                        <span className={styles.topicRowName}>{tp.title}</span>
                                                        {typeof tp.questionCount === "number" && (
                                                            <span className={styles.topicRowCount}>
                                                                {t("pages.examBaseForm.questionsShort", { count: tp.questionCount })}
                                                            </span>
                                                        )}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        );
                                    })
                                )}
                            </div>

                            {selectedTopics.length > 0 && (
                                <div className={styles.pickerMeta}>
                                    {t("pages.examBaseForm.availableQuestions", { count: availableQuestions })}
                                </div>
                            )}

                            {spansMultipleSubjects && (
                                <div className={styles.multiNote} role="note">
                                    {t("pages.examBaseForm.multiSubjectNote")}
                                </div>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t("pages.examBaseForm.title")}</label>
                            <input
                                type="text"
                                name="title"
                                value={values.title}
                                onChange={onChange}
                                className={styles.input}
                                placeholder={t("pages.examBaseForm.titlePlaceholder")}
                                autoComplete="off"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>
                                <FiClock size={14} className={styles.labelIcon}/>
                                {t("pages.examBaseForm.duration")}
                            </label>
                            <input
                                type="number"
                                name="durationMinutes"
                                value={values.durationMinutes}
                                onChange={onChange}
                                className={styles.input}
                                placeholder={t("pages.examBaseForm.durationPlaceholder")}
                                min={1}
                                max={300}
                            />
                            {durationExceedsMax ? (
                                <span style={{display: "block", marginTop: 4, fontSize: 12, color: "#dc2626"}}>
                                    {t("common.durationMax")}
                                </span>
                            ) : formatMinutesAsHours(values.durationMinutes, t) ? (
                                <span style={{display: "block", marginTop: 4, fontSize: 12, color: "#6b7280"}}>
                                    = {formatMinutesAsHours(values.durationMinutes, t)}
                                </span>
                            ) : null}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>
                                <FiPercent size={14} className={styles.labelIcon}/>
                                {t("pages.examBaseForm.passRate")}
                            </label>
                            <input
                                type="number"
                                name="passRate"
                                value={values.passRate}
                                onChange={onChange}
                                className={styles.input}
                                placeholder={t("pages.examBaseForm.passRatePlaceholder")}
                                min={1}
                                max={100}
                                step={1}
                            />
                        </div>

                        {error && (
                            <div className={styles.error} role="alert">
                                {error}
                            </div>
                        )}
                    </div>

                    <div className={styles.actions}>
                        <button type="button" onClick={onClose} className={styles.secondaryBtn}>
                            {t("common.cancel")}
                        </button>
                        <button
                            type="submit"
                            disabled={submitDisabled}
                            className={styles.primaryBtn}
                        >
                            {saving ? t("pages.examBaseForm.saving") : isCreateMode ? t("pages.examBaseForm.addBtn") : t("pages.examBaseForm.saveBtn")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ExamBaseFormModal;
